import { Buffer } from 'buffer';
import { Box, Grid, TextField } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { teceoSignatureFontFamily } from '../../../../Theme';
import useWidth from '../../../../components/Hooks/useWidth';
import LangEnum from '../../../../lang/LangEnum';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { validateOrderConditionsQuery } from '../../contexts/order.utils';
import { useSignatureContext } from '../contexts/signature.context';
import useCanvasStyle from './useCanvasStyle';

interface DigitTabPanelProps {
  orderId: string;
}

const DigitTabPanel = ({ orderId }: DigitTabPanelProps) => {
  const { changeHandleGenerateSignedContractPdf, changeCleanSign, changeHasSignInput } =
    useSignatureContext();
  const refCanvas: MutableRefObject<HTMLCanvasElement | null> = useRef(null);
  const refTextInput: MutableRefObject<HTMLInputElement | null> = useRef(null);
  const canvasTxt: MutableRefObject<CanvasRenderingContext2D | null> = useRef(null);
  const [text, setText] = useState('');
  const [canvasHeight, setCanvasHeight] = useState(0);
  const [showCanvas, setShowCanvas] = useState(true);
  const { canvasClasses, screenHeight, screenWidth } = useCanvasStyle();
  const { mobileWidth } = useWidth();
  const { handleMessage } = snackbarStore;
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const refetchValidateConditionsQuery = useCallback(() => {
    queryClient.refetchQueries(validateOrderConditionsQuery(orderId, team?.id));
  }, [queryClient, orderId, team?.id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newText = e.target.value;
    changeHasSignInput(newText !== '');
    setText(newText);
  };

  const handleClick = () => {
    refTextInput.current!.focus();
  };

  const getSign = useCallback((): Buffer | undefined => {
    if (text.trim().length === 0) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.ORDER_CONTRACT_TAB_DIGIT_WARNING_EMPTY_STRING),
      });
      return;
    }

    const canvas = refCanvas.current!;
    const data = canvas.toDataURL().replace(/^data:image\/\w+;base64,/, '');
    return Buffer.from(data, 'base64');
  }, [handleMessage, text, translate]);

  const digitTopFromCanvas = useCallback(
    (canvas: HTMLCanvasElement | null): number => {
      if (canvas) {
        const y = canvasHeight / 2;
        return y + (mobileWidth ? 11 : 25);
      }
      return 0;
    },
    [canvasHeight, mobileWidth],
  );

  const digitFont = useCallback((): string => {
    const fontSize = Math.min(screenHeight, screenWidth) / (mobileWidth ? 3 : 3.5);
    return `${fontSize}px ${teceoSignatureFontFamily}`;
  }, [mobileWidth, screenHeight, screenWidth]);

  useEffect(() => {
    const canvas = refCanvas.current!;
    canvasTxt.current = canvas.getContext('2d')!;
    refTextInput.current!.focus();
  }, []);

  useEffect(() => {
    const canvas = refCanvas.current!;
    canvasTxt.current!.font = digitFont();
    canvasTxt.current!.clearRect(0, 0, canvas.width, canvas.height);
    canvasTxt.current!.fillText(text, 15, digitTopFromCanvas(canvas));
    setCanvasHeight(canvas.height);
  }, [digitFont, text, digitTopFromCanvas, mobileWidth, showCanvas, screenWidth, screenHeight]);

  useEffect(() => {
    changeHandleGenerateSignedContractPdf(getSign, refetchValidateConditionsQuery);
    changeCleanSign(() => {
      setText('');
      refTextInput.current!.value = '';
    });
  }, [
    getSign,
    changeCleanSign,
    refetchValidateConditionsQuery,
    changeHandleGenerateSignedContractPdf,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const isMobileKeyboardShowing = window.innerHeight - 120 < window.innerWidth;
      setShowCanvas(!isMobileKeyboardShowing);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
      }}
    >
      <Grid container wrap="nowrap" spacing={1} style={{ height: '100%' }} direction="column">
        <Grid
          size={{
            xs: showCanvas ? 'grow' : 'auto',
            md: 'auto',
          }}
        >
          <Box
            sx={{
              pt: showCanvas ? 4 : 1,
            }}
          >
            <canvas
              style={{ display: showCanvas ? 'block' : 'none' }}
              onClick={handleClick}
              className={canvasClasses.canvas}
              width={screenWidth}
              height={screenHeight}
              ref={refCanvas}
            />
          </Box>
        </Grid>
        <Grid
          size={{
            xs: 'auto',
            md: 'grow',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: '478px',
                pb: showCanvas ? 4 : 0,
                pt: showCanvas ? 0 : 2,
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                placeholder={translate(LangEnum.ORDER_CONTRACT_DIGIT_SIGN_LABEL)}
                inputRef={refTextInput}
                onChange={handleChange}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DigitTabPanel;
